<template>
    <Panel title="Quote" :loading="loading" @closePanel="$emit('closePanel')">
        <TypesIcons v-if="quote && quote.types" :types="quote.types" class="mb-1" position="left" />

        <Tabs v-if="quote" @selectTab="handleSelectTab">
            <!-- Quote -->
            <Tab name="Details" :selected="true">
                <div class="mt-1">
                    <div class="list-group mb-3">
                        <template v-for="(file, index) in quote.importer_ids">
                            <div
                                v-if="isDownloadLoading(file)"
                                class="list-group-item list-group-item-light"
                                :key="`${file}-loading`"
                            >
                                <Spinner />
                            </div>
                            <a
                                v-else
                                href="#"
                                @click.prevent="downloadQuote(file)"
                                class="list-group-item list-group-item-light list-group-item-action"
                                :key="file"
                            >
                                <Icon icon="download" class="me-2" /> Download quote file {{ index + 1 }}
                            </a>
                        </template>
                    </div>
                </div>
                <QuoteStatus class="mb-2" :quote="quote" />
                <div class="progress-container">
                    <Steps :steps="stepsArray" :active="activeSteps" />
                </div>
                <Sheet name="Quote" class="mb-2">
                    <Row name="Date">{{ quote.created_at | formatDate }}</Row>
                    <Row name="Reference" v-if="quote.reference">{{ quote.reference }}</Row>
                    <Row name="Total">{{ quote.amount_total | currency }}</Row>
                    <Row v-if="quote.metadata && Object.keys(quote.metadata).length > 0" name="Metadata">
                        <RawOutput :item="quote.metadata" />
                    </Row>
                    <Row v-if="!!quote.finalized_at" name="Finalized at">{{ quote.finalized_at | formatDate }}</Row>
                    <Row v-if="!!quote.accepted_at" name="Accepted at">{{ quote.accepted_at | formatDate }}</Row>
                    <Row name="Funded at">
                        <span v-if="quote.funded_at">{{ quote.funded_at | formatDate }}</span
                        ><span v-else>Not funded</span>
                        <span v-if="quote.funded_by"> ({{ quote.funded_by }} funding)</span>
                    </Row>
                    <Row name="Auto-process"
                        ><Boolean :value="!quote.block_auto_process" textOn="Allowed" textOff="Blocked"
                    /></Row>
                    <template v-if="!!quote.processing_started_at">
                        <Row name="Processed started at">{{
                            quote.processing_started_at | formatDate('DD/MM/YYYY HH:mm:ss')
                        }}</Row>
                        <Row name="Processed finished at">
                            <Spinner v-if="!quote.processing_finished_at" />
                            <span v-else>{{ quote.processing_finished_at | formatDate('DD/MM/YYYY HH:mm:ss') }}</span>
                        </Row>
                    </template>
                </Sheet>

                <Sheet name="Billing account" v-if="billingAccount">
                    <Row name="Name"
                        ><a href="#" @click.prevent="openBillingAccount(billingAccount)">{{
                            billingAccount.name
                        }}</a></Row
                    >
                    <Row name="City">{{ billingAccount.city }} {{ billingAccount.country_iso }}</Row>
                </Sheet>

                <Sheet name="Invoice" v-if="invoice">
                    <Row name="ID"
                        ><a href="#" @click.prevent="openInvoice(invoice)">{{ invoice.id }}</a></Row
                    >
                    <Row name="Date">{{ invoice.created_at | formatDate }}</Row>
                    <Row name="Total">{{ invoice.total | currency }}</Row>
                    <Row name="Status"><InvoiceStatus :invoice="invoice" /></Row>
                </Sheet>
                <ID name="Hexeko" :value="quote.id" />
            </Tab>

            <!-- LineRow -->
            <Tab name="Lines">
                <Table striped borderless :loading="loadingGroupedLines">
                    <template v-slot:thead>
                        <tr>
                            <th width="60%" colspan="2">Product</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Amount</th>
                        </tr>
                    </template>
                    <LineRow
                        v-for="(line, index) in lines"
                        :icon="line.type"
                        :quantity="line.quantity"
                        :title="line.product"
                        :amount="line.amount_total"
                        :amount_unit="line.amount_unit ? line.amount_unit : line.amount_total"
                        :key="'line-' + index"
                    >
                        <code class="text-muted">{{ line.slug }}</code>
                    </LineRow>
                </Table>
            </Tab>

            <!-- Lines detail -->
            <Tab name="Lines detail">
                <Datatable
                    name="Lines detail"
                    striped
                    borderless
                    @search="fetchLinesDetail"
                    :pagination.sync="pagination"
                    :loading="loadingLinesDetail"
                    hideActions
                    :hover="false"
                >
                    <template v-slot:filters>
                        <Button
                            class="me-1"
                            @click="toggleProcessed"
                            :color="filterProcessed ? 'success' : filterProcessed === false ? 'danger' : 'secondary'"
                            small
                        >
                            Processed
                        </Button>
                    </template>
                    <template v-slot:thead>
                        <tr>
                            <th width="60%" colspan="2">Product</th>
                            <th>Quantity</th>
                            <th>Price</th>
                            <th>Amount</th>
                        </tr>
                    </template>
                    <LineRow
                        v-for="(line, index) in linesDetail"
                        :icon="line.type"
                        :title="line.product.description"
                        :quantity="line.quantity"
                        :amount="line.amount"
                        :amount_unit="line.amount"
                        :key="'lineDetail-' + index"
                    >
                        <div v-if="line.metadata && line.metadata.start_date">
                            Start date: {{ line.metadata.start_date | formatDate('DD/MM/YYYY') }}
                        </div>
                        <div v-if="line.metadata && line.metadata.end_date">
                            End date: {{ line.metadata.end_date | formatDate('DD/MM/YYYY') }}
                        </div>
                        <div v-if="line.user">
                            <a href="#" @click.prevent="openUser(line.user)" class="text-muted">
                                <Icon icon="user" class="me-1" />
                                <span v-if="line.user.first_name || line.user.last_name"
                                    >{{ line.user.first_name }} {{ line.user.last_name }}</span
                                >
                                <span v-else>User {{ line.user.id }}</span>
                            </a>
                        </div>
                        <div v-if="line.metadata && line.metadata.external_id">
                            External ID: {{ line.metadata.external_id }}
                        </div>
                    </LineRow>
                </Datatable>
            </Tab>
        </Tabs>

        <!-- Actions -->
        <template v-slot:actions v-if="!loading && quote && $store.getters.userHasPermission('manageQuotes')">
            <Dropdown small title="Actions" position="right">
                <DropdownItem v-if="!quote.is_processing" icon="calendar" @click="openModal('updateDates')"
                    >Update dates</DropdownItem
                >
                <DropdownItem
                    confirm
                    @confirm="toggleAutoProcess"
                    :disabled="loadingAutoProcess"
                    :icon="confirmAutoProcessIcon"
                    :title="confirmAutoProcessTitle"
                    :text="confirmAutoProcessText"
                    >{{ confirmAutoProcessTitle }}</DropdownItem
                >
                <DropdownItem
                    v-if="!quote.finalized_at"
                    confirm
                    @confirm="finalizeQuote"
                    icon="check-circle"
                    linkClasses="text-success"
                    title="Finalise quote"
                    text="Are you sure you want to finalise this quote?"
                    >Finalise</DropdownItem
                >
                <DropdownItem
                    v-if="quote.finalized_at && !quote.accepted_at"
                    confirm
                    @confirm="acceptQuote"
                    icon="check-circle"
                    linkClasses="text-success"
                    title="Accept quote"
                    text="Are you sure you want to accept this quote?"
                    >Accept</DropdownItem
                >
                <DropdownItem
                    v-if="quote.is_processed"
                    confirm
                    @confirm="unprocessQuote"
                    icon="undo"
                    linkClasses="text-danger"
                    title="Unprocess Quote"
                    text="Are you sure you want to unprocess this quote? Don't forget to manually add a credit note for this adjustment."
                    >Unprocess</DropdownItem
                >
                <DropdownItem
                    v-else-if="quote.invoice && !quote.invoice.canceled_at"
                    confirm
                    @confirm="processQuote"
                    icon="undo"
                    :linkClasses="quote.processing_started_at ? 'text-warning' : 'text-success'"
                    title="Process Quote"
                    :disabled="!!quote.processing_started_at || processLoading"
                    text="Are you sure you want to process this quote? Don't forget to manually add a credit note for this adjustment."
                    >{{
                        quote.processing_started_at && quote.processing_finished_at
                            ? 'Processed'
                            : quote.processing_started_at
                              ? 'Processing…'
                              : 'Process'
                    }}</DropdownItem
                >
                <DropdownItem
                    v-if="!quote.invoice"
                    confirm
                    @confirm="deleteQuote"
                    icon="trash-alt"
                    linkClasses="text-danger"
                    title="Delete quote"
                    text="Are you sure you want to delete this quote?"
                    >Delete</DropdownItem
                >
            </Dropdown>
        </template>

        <!-- Modal -->
        <Modal v-if="isModalOpen('updateDates')" @close="closeModal('updateDates')" title="Update dates of the quote">
            <UpdateDates :quote="quote" @submitted="datesUpdated" />
        </Modal>
    </Panel>
</template>

<script>
    import {
        Alerts,
        Boolean,
        Datatable,
        Dropdown,
        DropdownItem,
        ID,
        Modal,
        Panel,
        Row,
        Sheet,
        Spinner,
        Steps,
        Tab,
        Table,
        Tabs,
        Button,
    } from '@tech_hexeko/design-system'
    import Modals from '@tech_hexeko/design-system/src/mixins/Modals'
    import { downloadFile } from '@tech_hexeko/shared/src/utils/query'
    import { formatError } from '@/plugins/errors.js'
    import InvoiceStatus from '@/components/Invoices/status'
    import LineRow from '@/components/layout/LineRow'
    import RawOutput from '@/components/RawOutput'
    import TypesIcons from '@/components/Types/Icons.vue'
    import UpdateDates from './UpdateDates'
    import QuoteStatus from './status'
    import { formatBoolean, toggleBoolean } from '@/utils/query'

    export default {
        name: 'QuotePanel',
        mixins: [Modals],
        components: {
            Boolean,
            Datatable,
            Dropdown,
            DropdownItem,
            ID,
            InvoiceStatus,
            LineRow,
            Modal,
            Panel,
            QuoteStatus,
            RawOutput,
            Row,
            Sheet,
            Spinner,
            Steps,
            Tab,
            Table,
            Tabs,
            TypesIcons,
            UpdateDates,
            Button,
        },
        props: {
            id: String,
        },
        data() {
            return {
                loading: true,
                quote: null,
                lines: [],
                linesDetail: [],
                loadingAutoProcess: false,
                loadingDownloadIds: [],
                loadingGroupedLines: true,
                loadingLinesDetail: true,
                pagination: {
                    total_pages: 0,
                    current_page: 1,
                },
                invoice: null,
                billingAccount: null,
                search: null,
                stepsArray: ['Created', 'Finalized', 'Accepted', 'Funded', 'Processed'],
                processLoading: false,
                filterProcessed: null,
            }
        },
        computed: {
            activeSteps() {
                if (this.quote.is_processed) return 4
                if (this.quote.funded_at) return 3
                if (this.quote.accepted_at) return 2
                if (this.quote.finalized_at) return 1
                return 0
            },
            confirmAutoProcessIcon() {
                return this.isAutoProcessBlocked ? 'play' : 'stop'
            },
            confirmAutoProcessText() {
                return this.isAutoProcessBlocked
                    ? 'Are you sure you want to allow the auto-process of this quote?'
                    : 'Are you sure you want to block the auto-process of this quote?'
            },
            confirmAutoProcessTitle() {
                return this.isAutoProcessBlocked ? 'Allow auto-process' : 'Block auto-process'
            },
            isAutoProcessBlocked() {
                return this.quote?.block_auto_process
            },
        },
        methods: {
            toggleProcessed() {
                this.filterProcessed = toggleBoolean(this.filterProcessed)
                this.pagination.current_page = 1
                this.fetchLinesDetail(this.search)
            },
            async unprocessQuote() {
                try {
                    this.loading = true
                    await this.$api.billing.quotes.unprocess(this.id, {
                        user_id: this.$store?.getters?.getUser?.id ? this.$store.getters.getUser.id : undefined,
                    })
                    Alerts.notificationSuccess(
                        'The quote is being unprocessed, you will receive an email when it is done'
                    )
                    await this.fetchData()
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.loading = false
                }
            },
            async acceptQuote() {
                try {
                    this.loading = true
                    await this.$api.billing.quotes.accept(this.id)
                    Alerts.notificationSuccess('The quote is in the process of being accepted')
                    // Delay to wait for the process
                    await new Promise((resolve) => setTimeout(resolve, 3000))
                    await this.fetchData()
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.loading = false
                }
            },
            async finalizeQuote() {
                try {
                    this.loading = true
                    await this.$api.billing.quotes.finalize(this.id)
                    Alerts.notificationSuccess('The quote is being finalised')
                    // Delay to wait for the process
                    await new Promise((resolve) => setTimeout(resolve, 3000))
                    await this.fetchData()
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.loading = false
                }
            },
            processQuote() {
                this.processLoading = true
                this.$api.billing.quotes
                    .process(this.id)
                    .then(() => {
                        Alerts.messageSuccess('Quote processing…')
                        this.fetchData()
                    })
                    .catch((error) => {
                        this.handleError(error)
                        if (error?.status === 422) {
                            this.askForceProcess()
                        }
                    })
                    .finally(() => {
                        this.processLoading = false
                    })
            },
            askForceProcess() {
                Alerts.askConfirmation({
                    title: 'Force Process',
                    html: 'Forcing this process is mandatory to proceed with this quote. This action will bypass all standard checks, including invoice payment and its cancellation status. Please ensure you fully understand the implications before proceeding.',
                    cancelButtonText: 'Cancel',
                    confirmButtonText: 'Force Process',
                    showCancelButton: true,
                    allowOutsideClick: true,
                    allowEscapeKey: true,
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.processLoading = true
                        this.$api.billing.quotes
                            .forceProcess(this.id)
                            .then(() => {
                                Alerts.messageSuccess('Quote processing…')
                                this.fetchData()
                            })
                            .catch((error) => {
                                this.handleError(error)
                            })
                            .finally(() => {
                                this.processLoading = false
                            })
                    }
                })
            },
            async deleteQuote() {
                try {
                    this.loading = true
                    await this.$api.billing.quotes.delete(this.id)
                    this.$emit('closePanel', { refresh: true })
                    Alerts.notificationSuccess('The quote has been deleted')
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.loading = false
                }
            },
            async fetchData() {
                try {
                    this.loading = true
                    this.quote = await this.$api.billing.quotes.show(this.id, { includes: ['invoice'] })
                    this.fetchGroupedLines()
                    this.fetchLinesDetail(this.search)
                    this.fetchBillingAccount(this.quote.billing_account_id)
                    if (this.quote.invoice_id) this.fetchInvoice(this.quote.invoice_id)
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.loading = false
                }
            },
            async fetchGroupedLines() {
                try {
                    this.loadingGroupedLines = true
                    const response = await this.$api.billing.quotes.getGroupedLines(this.id)
                    this.lines = response.data
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.loadingGroupedLines = false
                }
            },
            async fetchLinesDetail(search) {
                try {
                    this.loadingLinesDetail = true
                    this.search = search
                    const response = await this.$api.billing.quotes.getLines(this.id, {
                        includes: ['user'],
                        per_page: 15,
                        page: this.pagination.current_page,
                        processed: formatBoolean(this.filterProcessed),
                        ...(this.search && { search: this.search }),
                    })
                    this.linesDetail = response.data
                    this.pagination = response.meta?.pagination
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.loadingLinesDetail = false
                }
            },
            async fetchInvoice(id) {
                try {
                    this.invoice = await this.$api.billing.invoices.show(id)
                } catch (error) {
                    this.handleError(error)
                }
            },
            async fetchBillingAccount(id) {
                try {
                    this.billingAccount = await this.$api.billing.billingAccounts.show(id)
                } catch (error) {
                    this.handleError(error)
                }
            },
            datesUpdated() {
                this.fetchData()
                this.closeModal('updateDates')
            },
            handleSelectTab(tab) {
                // Reset search when switching to Lines detail tab if necessary
                if (tab.name === 'Lines detail' && this.search) this.fetchLinesDetail()
            },
            openInvoice(invoice) {
                this.openPanel('InvoicePanel', invoice.id)
            },
            openBillingAccount(account) {
                this.openPanel('BillingAccountPanel', account.id)
            },
            openUser(user) {
                this.openPanel('UserPanel', user.id)
            },
            async downloadQuote(id) {
                try {
                    this.loadingDownloadIds.push(id)
                    const getFile = await this.$importerApi.files.downloadAdmin(id)
                    downloadFile(getFile, `${this.quote.id}`)
                } catch (error) {
                    this.handleError(error)
                } finally {
                    const idIndex = this.loadingDownloadIds.indexOf(id)
                    this.loadingDownloadIds.splice(idIndex, 1)
                }
            },
            async toggleAutoProcess() {
                try {
                    this.loadingAutoProcess = true
                    if (this.isAutoProcessBlocked) {
                        await this.$api.billing.quotes.unblockAutoProcess(this.quote.id)
                    } else {
                        await this.$api.billing.quotes.blockAutoProcess(this.quote.id)
                    }
                    Alerts.notificationSuccess('The auto-process has been updated')
                    await this.fetchData()
                } catch (error) {
                    this.handleError(error)
                } finally {
                    this.loadingAutoProcess = false
                }
            },
            handleError(error) {
                const errorTxt = formatError(error)
                Alerts.notificationError(errorTxt)
            },
            isDownloadLoading(id) {
                return this.loadingDownloadIds.includes(id)
            },
        },
        mounted() {
            this.fetchData()
        },
    }
</script>
<style lang="css" scoped>
    .progress-container {
        margin: 1rem auto 2.6rem auto;
        width: 82%;
    }
</style>
